import { useState, useEffect, useRef } from "react";
import api, { apiError, formatApiError, mediaUrl } from "@/lib/api";
import { useInstall } from "@/components/InstallPrompt";
import IosInstallSheet from "@/components/IosInstallSheet";
import { useAuth } from "@/context/AuthContext";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useI18n } from "@/context/I18n";
import { toast } from "sonner";
import { Camera, Check, Copy, Disc3, Download, Image as ImageIcon, KeyRound, Library, Loader2, Lock, Plus, Send, Smartphone, StickyNote, Trash2, User, Users, Wand2 } from "lucide-react";

export default function Settings() {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [sp, setSp] = useState(false);
  const [spw, setSpw] = useState(false);

  const inputCls = "mt-1.5 w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF2A4D] focus:ring-1 focus:ring-[#FF2A4D]";

  const saveProfile = async (e) => {
    e.preventDefault(); setSp(true);
    try { const { data } = await api.patch("/auth/me", { name, email }); setUser(data); toast.success(t("profile_updated")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); } finally { setSp(false); }
  };
  const savePassword = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error(t("passwords_no_match")); return; }
    setSpw(true);
    try { await api.patch("/auth/me", { current_password: current, new_password: next }); toast.success(t("password_updated")); setCurrent(""); setNext(""); setConfirm(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); } finally { setSpw(false); }
  };

  return (
    <div className="p-8 max-w-2xl animate-fade-up">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A4D]">{t("nav_settings")}</p>
      <h1 className="font-display text-3xl font-black tracking-tighter mt-2">{t("your_account")}</h1>

      <InstallPanel />
      <PlaybackPanel />

      <form onSubmit={saveProfile} className="mt-8 bg-[#121212] border border-white/5 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4"><User className="w-4 h-4 text-[#FF2A4D]" /><h3 className="font-display font-bold">{t("profile")}</h3></div>
        <AvatarPanel />
        <label className="text-sm text-zinc-400">{t("name")}</label>
        <input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        <label className="text-sm text-zinc-400 block mt-4">{t("email")}</label>
        <input data-testid="settings-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <button data-testid="settings-save-profile" disabled={sp} className="mt-5 bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-6 py-2.5 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-60">
          {sp ? <Loader2 className="w-4 h-4 animate-spin" /> : t("save_changes")}
        </button>
      </form>

      <form onSubmit={savePassword} className="mt-6 bg-[#121212] border border-white/5 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4"><Lock className="w-4 h-4 text-[#FF2A4D]" /><h3 className="font-display font-bold">{t("change_password")}</h3></div>
        <label className="text-sm text-zinc-400">{t("current_password")}</label>
        <input data-testid="settings-current-pass" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} />
        <label className="text-sm text-zinc-400 block mt-4">{t("new_password")}</label>
        <input data-testid="settings-new-pass" type="password" value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} />
        <label className="text-sm text-zinc-400 block mt-4">{t("confirm_password")}</label>
        <input data-testid="settings-confirm-pass" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
        <button data-testid="settings-save-pass" disabled={spw} className="mt-5 bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-6 py-2.5 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-60">
          {spw ? <Loader2 className="w-4 h-4 animate-spin" /> : t("update_password")}
        </button>
      </form>

      {user?.role === "admin" && <OnlinePanel />}
      {user?.role === "admin" && <StatsPanel />}
      {user?.role === "admin" && <DiscographyPanel />}
      {user?.role === "admin" && <ArtworkPanel />}
      {user?.role === "admin" && <ShortAlbumsPanel />}
      {user?.role === "admin" && <InvitesPanel />}
    </div>
  );
}

function PlaybackPanel() {
  const { t } = useI18n();
  const { crossfade, setCrossfade, keepAwake, setKeepAwake, keepAwakeSupported } = useMusicPlayer();

  // A switch with no text inside it needs a name of its own, or a screen
  // reader announces nothing but "button". role/aria-checked are what make it
  // read as a switch rather than a toggle button.
  const Toggle = ({ on, onClick, testId, label }) => (
    <button data-testid={testId} onClick={onClick} role="switch" aria-checked={on} aria-label={label}
      className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${on ? "bg-[#FF2A4D]" : "bg-white/15"}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-6" : "left-0.5"}`} />
    </button>
  );

  return (
    <div className="mt-6 bg-[#121212] border border-white/5 rounded-xl p-6" data-testid="playback-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-display font-bold">{t("crossfade")}</h3>
          <p className="text-sm text-zinc-500 mt-0.5">4s</p>
        </div>
        <Toggle on={crossfade} onClick={() => setCrossfade(!crossfade)} testId="crossfade-toggle" label={t("crossfade")} />
      </div>

      {keepAwakeSupported && (
        <div className="flex items-center justify-between gap-4 mt-5 pt-5 border-t border-white/5" data-testid="keep-awake-row">
          <div>
            <h3 className="font-display font-bold">{t("keep_awake")}</h3>
            <p className="text-sm text-zinc-500 mt-0.5">{t("keep_awake_desc")}</p>
          </div>
          <Toggle on={keepAwake} onClick={() => setKeepAwake(!keepAwake)} testId="keep-awake-toggle" label={t("keep_awake")} />
        </div>
      )}
    </div>
  );
}

function InstallPanel() {
  const { t } = useI18n();
  const { install, installed, canInstall } = useInstall();
  const [showIos, setShowIos] = useState(false);

  if (installed) return null;

  const onClick = async () => {
    if (canInstall) {
      const result = await install();
      // A stale prompt does nothing at all, which reads as a broken button.
      if (result !== "unavailable") return;
    }
    // No prompt to fire: Chrome offers it once and not at all once installed,
    // and iOS has no install API. Either way the browser menu can do it, so
    // show where it is instead of leaving the button dead.
    setShowIos((v) => !v);
  };

  return (
    <div className="mt-8 bg-gradient-to-br from-[#FF2A4D]/20 to-[#121212] border border-white/10 rounded-xl p-6" data-testid="install-panel">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#FF2A4D] flex items-center justify-center shrink-0"><Smartphone className="w-6 h-6" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold">{t("install_title")}</h3>
          <p className="text-sm text-zinc-400">{t("install_desc")}</p>
        </div>
        <button onClick={onClick} data-testid="install-btn" className="bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 shrink-0">
          <Download className="w-4 h-4" /> {t("install_app")}
        </button>
      </div>
      {showIos && <IosInstallSheet onClose={() => setShowIos(false)} />}
    </div>
  );
}

function ArtworkPanel() {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const timer = useRef(null);

  const poll = () => api.get("/music/enrich/status").then((r) => {
    setStatus(r.data);
    if (r.data.running) timer.current = setTimeout(poll, 2500);
    else if (r.data.finished_at) { toast.success(t("artwork_done")); }
  }).catch(() => {});

  // `poll` reschedules itself; this only has to start it once, on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { poll(); return () => clearTimeout(timer.current); }, []);

  const run = async () => {
    try { await api.post("/music/enrich"); toast.success(t("fixing")); setTimeout(poll, 800); }
    catch (e) { toast.error(apiError(e)); }
  };

  const running = status?.running;
  const pct = status && status.total ? Math.round((status.done / status.total) * 100) : 0;

  return (
    <div className="mt-6 bg-[#121212] border border-white/5 rounded-xl p-6" data-testid="artwork-panel">
      <div className="flex items-center gap-2 mb-1"><ImageIcon className="w-4 h-4 text-[#FF2A4D]" /><h3 className="font-display font-bold">{t("artwork_title")}</h3></div>
      <p className="text-sm text-zinc-500 mb-4">{t("artwork_desc")}</p>
      <button onClick={run} disabled={running} data-testid="fix-artwork-btn" className="bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-lg px-5 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {running ? t("fixing") : t("fix_artwork")}
      </button>
      {running && (
        <div className="mt-4">
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[#FF2A4D] transition-all" style={{ width: `${pct}%` }} /></div>
          <p className="text-xs text-zinc-500 mt-2" data-testid="artwork-progress">{status.done}/{status.total} · {pct}%</p>
        </div>
      )}
      {!running && status?.finished_at && (
        <p className="text-xs text-zinc-500 mt-3">+{status.updated_artists} artists · +{status.updated_albums} covers · +{status.updated_tracks} tracks · {(status.merged_albums || 0) + (status.merged_tracks || 0)} merged</p>
      )}
    </div>
  );
}

/**
 * What the catalogue holds, and what it is costing.
 *
 * The database is billed by row rather than by size, so the total matters as
 * much as the music does — and a third of it was once cache: artwork and
 * genre answers kept so they would not be asked twice, and a table of artist
 * spellings the enrichment rebuilds every run regardless. None of it is
 * content, so it can go whenever the room is wanted back.
 */
/**
 * Who is listening right now, and when everyone else was last around.
 *
 * Admin-only. The gate here is cosmetic — /auth/online is behind requireAdmin,
 * which is what actually keeps other listeners from reading it.
 */
function OnlinePanel() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState("");

  const load = () =>
    api.get("/auth/online")
      .then((r) => { setData(r.data); setError(""); })
      // Without this the whole panel used to vanish on a failed request, which
      // looks exactly like "the feature was never deployed".
      .catch((e) => setError(apiError(e)));

  useEffect(() => {
    load();
    // Presence goes stale after three minutes, so half that keeps the dots
    // honest without hammering the gateway while the tab sits open.
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, []);

  const remove = async (target) => {
    const label = target.name ? `${target.name} (${target.email})` : target.email;
    if (!window.confirm(`${t("remove_user_confirm")}\n\n${label}`)) return;
    setRemoving(target.id);
    try {
      await api.delete(`/auth/users/${target.id}`);
      toast.success(`${t("user_removed")}: ${target.email}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRemoving("");
    }
  };

  // Intl does the pluralisation and the wording in both languages, which beats
  // carrying "hace N minutos" variants in the dictionary.
  const relative = (iso) => {
    const seconds = Math.round((Date.parse(iso) - Date.now()) / 1000);
    if (!Number.isFinite(seconds)) return "";
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
    const units = [["day", 86400], ["hour", 3600], ["minute", 60]];
    for (const [unit, size] of units) {
      if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
    }
    return t("just_now");
  };

  if (error) {
    return (
      <div className="mt-6 bg-[#121212] border border-white/5 rounded-xl p-6" data-testid="online-panel">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-[#FF2A4D]" />
          <h3 className="font-display font-bold">{t("listeners")}</h3>
        </div>
        <p className="text-sm text-[#FF2A4D]" data-testid="online-error">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const online = data.users.filter((u) => u.online);

  return (
    <div className="mt-6 bg-[#121212] border border-white/5 rounded-xl p-6" data-testid="online-panel">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#FF2A4D]" />
          <h3 className="font-display font-bold">{t("listeners")}</h3>
        </div>
        <span data-testid="online-count" className="text-sm font-semibold tabular-nums text-zinc-400">
          {online.length}/{data.accounts}
        </span>
      </div>
      <p className="text-sm text-zinc-500 mb-4">{t("listeners_desc")}</p>

      {online.length === 0 && (
        <p className="text-sm text-zinc-600 mb-4" data-testid="nobody-online">{t("nobody_online")}</p>
      )}

      <ul className="space-y-1">
        {data.users.map((u) => {
          // An admin cannot be deleted and neither can you, so the button is
          // absent rather than present-and-failing.
          const deletable = u.role !== "admin" && u.id !== String(user?.id);
          return (
            <li key={u.id} data-testid={`presence-${u.id}`}
              className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
              <span className="relative w-9 h-9 rounded-full bg-[#FF2A4D] flex items-center justify-center text-sm font-bold overflow-hidden shrink-0">
                {u.avatar_url
                  ? <img src={mediaUrl(u.avatar_url)} alt="" className="w-full h-full object-cover" />
                  : (u.name || u.email)[0].toUpperCase()}
                <span
                  aria-hidden="true"
                  className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#121212] ${u.online ? "bg-emerald-400" : "bg-zinc-600"}`}
                />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{u.name || u.email}</p>
                <p className="text-xs text-zinc-500 truncate">{u.email}</p>
              </div>
              <div className="text-right shrink-0">
                <p className={`text-xs ${u.online ? "text-emerald-400 font-semibold" : "text-zinc-500"}`}>
                  {u.online ? t("online_now") : u.last_seen ? relative(u.last_seen) : t("never_seen")}
                </p>
                {/* How long they have had the account — the number a trial is judged on. */}
                <p className="text-[11px] text-zinc-600">
                  {u.created_at ? `${t("joined")} ${relative(u.created_at)}` : ""}
                </p>
              </div>
              {deletable ? (
                <button
                  onClick={() => remove(u)} disabled={removing === u.id}
                  data-testid={`remove-user-${u.id}`} title={t("remove_user")} aria-label={t("remove_user")}
                  className="p-2 rounded-lg text-zinc-600 hover:text-[#FF2A4D] hover:bg-white/5 disabled:opacity-50 shrink-0"
                >
                  {removing === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              ) : (
                <span className="w-8 shrink-0" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StatsPanel() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/music/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const clearCache = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/music/cache/clear");
      toast.success(`${t("cache_cleared")}: ${data.freed.toLocaleString()}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!stats) return null;

  const cell = (value, label) => (
    <div className="flex-1 min-w-0">
      <p className="font-heading text-2xl font-bold tabular-nums">{value.toLocaleString()}</p>
      <p className="text-xs text-zinc-500 truncate">{label}</p>
    </div>
  );

  return (
    <div className="mt-6 bg-[#121212] border border-white/5 rounded-xl p-6" data-testid="stats-panel">
      <div className="flex gap-6">
        {cell(stats.artists, t("artists"))}
        {cell(stats.albums, t("albums"))}
        {cell(stats.tracks, t("tracks"))}
      </div>

      {typeof stats.records === "number" && (
        <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between gap-4">
          <p className="text-xs text-zinc-500 min-w-0">
            {stats.records.toLocaleString()} {t("db_records")}
            {stats.cache > 0 && ` · ${stats.cache.toLocaleString()} ${t("db_cache")}`}
          </p>
          <button
            onClick={clearCache}
            disabled={busy || !stats.cache}
            data-testid="clear-cache-btn"
            className="shrink-0 text-sm px-3 py-1.5 rounded-lg border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40"
          >
            {busy ? t("clearing") : t("clear_cache")}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Importing from the record instead of from a search.
 *
 * Queue an artist by name and the job reads their real discography — the
 * albums they released and what is on each one — before looking for a single
 * video. Nothing that is not on a record can get in, which is the opposite of
 * how this catalogue was filled until now, and why it ended up holding a news
 * piece about the Taliban and eighteen Morad songs under the name of his
 * album.
 */
function DiscographyPanel() {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/music/discography").then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const artist = name.trim();
    if (!artist) return;
    try {
      await api.post("/music/discography/queue", { artists: [artist] });
      setName("");
      toast.success(`${t("disco_queued")}: ${artist}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggle = async () => {
    if (!state) return;
    try {
      const { data } = await api.put("/music/discography", { enabled: !state.enabled });
      setState((s) => ({ ...s, ...data }));
    } catch (err) { toast.error(apiError(err)); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/music/discography/run");
      setState(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  // Puts the artists already in the catalogue through the same job, which is
  // what repairs everything that arrived before it existed.
  const queueCatalog = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/music/discography/queue/catalog");
      toast.success(`${t("disco_queued")}: ${data.added}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 bg-[#121212] border border-white/5 rounded-xl p-6" data-testid="discography-panel">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Library className="w-4 h-4 text-[#FF2A4D]" />
            <h3 className="font-display font-bold">{t("disco_title")}</h3>
          </div>
          <p className="text-sm text-zinc-500">{t("disco_desc")}</p>
        </div>
        <button
          data-testid="disco-toggle"
          onClick={toggle}
          role="switch"
          aria-checked={!!state?.enabled}
          aria-label={t("disco_title")}
          className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${state?.enabled ? "bg-[#FF2A4D]" : "bg-white/15"}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${state?.enabled ? "left-6" : "left-0.5"}`} />
        </button>
      </div>

      <form onSubmit={add} className="mt-4 flex gap-2">
        <input
          data-testid="disco-artist-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("disco_placeholder")}
          className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF2A4D]"
        />
        <button data-testid="disco-add" className="bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> {t("disco_add")}
        </button>
      </form>

      {state && (
        <div className="mt-3 text-xs text-zinc-500 space-y-1" data-testid="disco-progress">
          <p>
            {state.queue?.length || 0} {t("disco_queued_count")} · {state.done_artists} {t("disco_artists_done")} ·
            {" "}{state.done_albums} {t("disco_albums_done")} · +{state.imported} {t("tracks")} · {state.missing} {t("disco_missing")}
          </p>
          {state.artist && (
            <p className="text-zinc-400">
              {state.artist.name}
              {state.album ? ` · ${state.album.title}` : ""}
              {state.albums?.length ? ` · ${state.albums.length} ${t("disco_albums_left")}` : ""}
            </p>
          )}
          {state.last_note && <p className="text-zinc-600 truncate">{state.last_note}</p>}
        </div>
      )}

      <button
        onClick={queueCatalog}
        disabled={busy}
        data-testid="disco-queue-catalog"
        className="mt-3 mr-4 text-sm text-zinc-400 hover:text-white underline disabled:opacity-60"
      >
        {t("disco_queue_catalog")}
      </button>
      <button
        onClick={runNow}
        disabled={busy}
        data-testid="disco-run"
        className="mt-3 text-sm text-zinc-400 hover:text-white underline disabled:opacity-60"
      >
        {busy ? t("short_albums_running") : t("disco_run_now")}
      </button>
    </div>
  );
}

/**
 * The standing job that fills out short albums.
 *
 * Doing them all in one sitting is not on offer: each album costs a handful
 * of third-party calls, an invocation has fifty to share with the other two
 * jobs, and there are over a thousand stubs. So this is a switch, and the
 * cron works through a couple every half hour until the catalogue runs out.
 */
function ShortAlbumsPanel() {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/music/albums/fill").then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!state) return;
    try {
      const { data } = await api.put("/music/albums/fill", { enabled: !state.enabled });
      setState((s) => ({ ...s, ...data }));
    } catch (e) { toast.error(apiError(e)); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/music/albums/fill/run");
      setState((s) => ({ ...s, ...data }));
      toast.success(`${t("short_albums_done")}: +${data.added_now ?? 0}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 bg-[#121212] border border-white/5 rounded-xl p-6" data-testid="short-albums-panel">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Disc3 className="w-4 h-4 text-[#FF2A4D]" />
            <h3 className="font-display font-bold">{t("short_albums_title")}</h3>
          </div>
          <p className="text-sm text-zinc-500">{t("short_albums_desc")}</p>
        </div>
        <button
          data-testid="short-albums-toggle"
          onClick={toggle}
          role="switch"
          aria-checked={!!state?.enabled}
          aria-label={t("short_albums_title")}
          className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${state?.enabled ? "bg-[#FF2A4D]" : "bg-white/15"}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${state?.enabled ? "left-6" : "left-0.5"}`} />
        </button>
      </div>

      {state && (
        <p className="text-xs text-zinc-500 mt-4" data-testid="short-albums-progress">
          {state.short_albums} {t("short_albums_pending")} · +{state.added} {t("tracks")} · {state.filled}/{state.checked}
          {state.last_album ? ` · ${state.last_album}` : ""}
        </p>
      )}

      <button
        onClick={runNow}
        disabled={busy}
        data-testid="short-albums-run"
        className="mt-3 text-sm text-zinc-400 hover:text-white underline disabled:opacity-60"
      >
        {busy ? t("short_albums_running") : t("short_albums_run_now")}
      </button>
    </div>
  );
}

function AvatarPanel() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pick = () => fileRef.current && fileRef.current.click();

  const upload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t("avatar_too_big")); return; }

    setBusy(true);
    try {
      const { data } = await api.post("/auth/avatar", file, {
        headers: { "Content-Type": file.type },
      });
      setUser((u) => ({ ...u, avatar_url: data.avatar_url }));
      toast.success(t("avatar_updated"));
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/avatar");
      setUser((u) => ({ ...u, avatar_url: null }));
      toast.success(t("avatar_removed"));
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const initial = (user && user.name ? user.name : "U")[0].toUpperCase();

  return (
    <div className="flex items-center gap-4 mb-6" data-testid="avatar-panel">
      <button onClick={pick} disabled={busy} title={t("avatar_change")}
        className="relative w-20 h-20 rounded-full bg-[#FF2A4D] flex items-center justify-center text-2xl font-bold overflow-hidden shrink-0 group">
        {user && user.avatar_url
          ? <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
          : initial}
        <span className="absolute inset-0 bg-black/55 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
        </span>
      </button>

      <div className="min-w-0">
        <p className="font-display font-bold truncate">{user && user.name}</p>
        <p className="text-sm text-zinc-500 truncate">{user && user.email}</p>
        <div className="flex items-center gap-3 mt-2">
          <button onClick={pick} disabled={busy} data-testid="avatar-upload"
            className="text-sm font-semibold text-[#FF2A4D] hover:underline disabled:opacity-60">
            {t("avatar_change")}
          </button>
          {user && user.avatar_url && (
            <button onClick={remove} disabled={busy} data-testid="avatar-remove"
              className="text-sm text-zinc-500 hover:text-white disabled:opacity-60">
              {t("avatar_remove")}
            </button>
          )}
        </div>
      </div>

      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={upload} className="hidden" data-testid="avatar-input" />
    </div>
  );
}

function InvitesPanel() {
  const { t } = useI18n();
  const [invites, setInvites] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(null);
  const [draftNote, setDraftNote] = useState("");

  const load = () => api.get("/invites").then((r) => setInvites(r.data.invites)).catch(() => setInvites([]));
  useEffect(() => { load(); }, []);

  const inviteUrl = (code) => `${window.location.origin}/register?code=${encodeURIComponent(code)}`;

  const create = async () => {
    setCreating(true);
    try { await api.post("/invites", { count: 1 }); toast.success(t("invite_created")); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/invites/send", { email: email.trim(), note: note.trim() || undefined });
      if (data.sent) {
        toast.success(`${t("invite_sent")} ${data.email}`);
      } else {
        // No mail provider configured yet: hand the admin a link to send.
        await navigator.clipboard.writeText(data.invite_url).catch(() => {});
        toast.message(t("invite_link_copied"), { description: data.invite_url, duration: 8000 });
      }
      setEmail(""); setNote(""); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSending(false); }
  };

  const revoke = async (code) => {
    if (!window.confirm(`${t("revoke")} ${code}?`)) return;
    try { await api.delete(`/invites/${code}`); toast.success(t("invite_revoked")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveNote = async (code) => {
    try { await api.patch(`/invites/${code}`, { note: draftNote }); setEditing(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const copy = async (code) => {
    await navigator.clipboard.writeText(inviteUrl(code));
    setCopied(code); setTimeout(() => setCopied(""), 1500);
  };

  const mailto = (inv) => {
    const body = [
      "You are invited to SONLYO.",
      "",
      `Invite code: ${inv.code}`,
      `Sign up: ${inviteUrl(inv.code)}`,
    ].join("\n");
    return `mailto:${inv.email || ""}?subject=${encodeURIComponent("Your SONLYO invitation")}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="mt-6 bg-[#121212] border border-white/5 rounded-xl p-6" data-testid="invites-panel">
      <div className="flex items-center justify-between mb-1 gap-3">
        <div className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#FF2A4D]" /><h3 className="font-display font-bold">{t("invites")}</h3></div>
        <button onClick={create} disabled={creating} data-testid="create-invite" className="bg-white/10 hover:bg-white/20 rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-60 shrink-0">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> {t("create_code")}</>}
        </button>
      </div>
      <p className="text-sm text-zinc-500 mb-4">{t("invites_desc")}</p>

      <form onSubmit={send} data-testid="invite-send-form" className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 mb-5 space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder={t("invite_email_ph")}
            className="flex-1 bg-[#050505] border border-white/10 rounded-lg px-3 py-2.5 text-sm placeholder-zinc-600 focus:outline-none focus:border-[#FF2A4D]"
          />
          <button type="submit" disabled={sending || !email.trim()} data-testid="invite-send"
            className="bg-[#FF2A4D] hover:bg-[#E61C3D] disabled:opacity-50 rounded-lg px-5 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 shrink-0">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("invite_send")}
          </button>
        </div>
        <input
          data-testid="invite-note" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder={t("invite_note_ph")}
          className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm placeholder-zinc-600 focus:outline-none focus:border-[#FF2A4D]"
        />
      </form>

      {invites === null ? <Loader2 className="w-5 h-5 animate-spin text-zinc-600" /> : invites.length === 0 ? (
        <p className="text-sm text-zinc-500">{t("no_codes")}</p>
      ) : (
        <div className="space-y-2" data-testid="invites-list">
          {invites.map((inv) => (
            <div key={inv.code} className="bg-[#0A0A0A] border border-white/5 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-3 flex-wrap">
                <code className="font-mono text-sm">{inv.code}</code>
                {inv.email && <span className="text-xs text-zinc-400 truncate max-w-[200px]">{inv.email}</span>}
                <span className={`text-xs ${inv.used_by ? "text-zinc-500" : inv.sent_at ? "text-emerald-400" : "text-[#FF2A4D]"}`}>
                  {inv.used_by ? `${t("used_by")} ${inv.used_by}` : inv.sent_at ? t("invite_sent_label") : t("available")}
                </span>
                <div className="flex items-center gap-1 ml-auto">
                  {!inv.used_by && (
                    <>
                      <a href={mailto(inv)} title={t("invite_open_mail")} className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/10"><Send className="w-4 h-4" /></a>
                      <button onClick={() => copy(inv.code)} title={t("invite_copy_link")} className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/10">
                        {copied === inv.code ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </>
                  )}
                  <button onClick={() => { setEditing(inv.code); setDraftNote(inv.note || ""); }} title={t("invite_note")}
                    className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/10"><StickyNote className="w-4 h-4" /></button>
                  <button onClick={() => revoke(inv.code)} data-testid={`revoke-invite-${inv.code}`} title={`${t("revoke")} ${inv.code}`} aria-label={`${t("revoke")} ${inv.code}`} className="p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-white/10"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              {editing === inv.code ? (
                <div className="flex gap-2 mt-2">
                  <input autoFocus value={draftNote} onChange={(e) => setDraftNote(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveNote(inv.code); if (e.key === "Escape") setEditing(null); }}
                    placeholder={t("invite_note_ph")}
                    className="flex-1 bg-[#050505] border border-white/10 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#FF2A4D]" />
                  <button onClick={() => saveNote(inv.code)} className="bg-white/10 hover:bg-white/20 rounded-lg px-3 text-sm font-semibold">{t("save")}</button>
                </div>
              ) : inv.note ? (
                <p className="text-xs text-zinc-500 mt-1.5 italic">{inv.note}</p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
